(() => {
  const SELECTORS = {
    form: 'form[action*="/cart/add"]',
    section: '[data-section-type="product"]',
    productJsonPrefix: 'ProductJson-'
  };

  const moneyFormat = (useCurrency) => {
    if (useCurrency) {
      return window.theme?.moneyFormatWithCurrency || window.theme?.moneyFormat || '${{amount}}';
    }
    return window.theme?.moneyFormat || '${{amount}}';
  };

  const formatMoney = (cents, useCurrency = false) => {
    const value = Number(cents || 0);
    if (window.theme?.Currency?.formatMoney) {
      return window.theme.Currency.formatMoney(value, moneyFormat(useCurrency));
    }

    const amount = (value / 100).toFixed(2);
    return useCurrency ? `${amount} ${window.Shopify?.currency?.active || ''}`.trim() : `$${amount}`;
  };

  const getSection = (form) => form.closest(SELECTORS.section) || form.closest('.shopify-section') || form.parentElement;

  const getProductData = (form) => {
    if (form._cfProductData) return form._cfProductData;

    const section = getSection(form);
    const sectionId = section?.dataset?.sectionId;
    const script = sectionId ? document.getElementById(`${SELECTORS.productJsonPrefix}${sectionId}`) : null;
    if (!script || !script.textContent.trim()) return null;

    try {
      form._cfProductData = JSON.parse(script.textContent);
      return form._cfProductData;
    } catch (error) {
      console.warn('Unable to parse crowdfund product JSON', error);
      return null;
    }
  };

  const getSelectedVariant = (form) => {
    const product = getProductData(form);
    const masterSelect = form.querySelector('select[name="id"]');
    const selectedId = masterSelect?.value;
    if (!product || !selectedId) return null;
    return product.variants?.find((variant) => String(variant.id) === String(selectedId)) || null;
  };

  const getCurrencyCodeEnabled = (form) => {
    const priceEl = form.querySelector('[data-cf-variant-price]');
    const flag = priceEl?.closest('[data-cf-money-format]')?.dataset?.cfMoneyFormat;
    if (flag === 'with_currency') return true;
    if (flag === 'money') return false;
    return false;
  };

  const syncButtonLabel = (form, variant, formattedPrice) => {
    const addToCartBtn = form.querySelector('[data-add-to-cart]');
    if (!addToCartBtn) return;

    if (!form.dataset.cfAtcLabel) {
      const labelEl = addToCartBtn.querySelector('[data-add-to-cart-label]');
      form.dataset.cfAtcLabel = (labelEl?.textContent || 'Back this product').trim();
    }

    const label = form.dataset.cfAtcLabel || 'Back this product';

    if (window.theme?.Helpers?.setAddToCartButtonLabel) {
      window.theme.Helpers.setAddToCartButtonLabel(addToCartBtn, label, { showPrices: true });
    } else {
      const labelEl = addToCartBtn.querySelector('[data-add-to-cart-label]');
      if (labelEl) labelEl.textContent = label;
    }

    const priceSep = addToCartBtn.querySelector('.product-form__cart-submit-price-sep');
    const priceEl = addToCartBtn.querySelector('[data-atc-price]');
    const compareEl = addToCartBtn.querySelector('[data-atc-compare-price]');
    const showCompare = Number(variant?.compare_at_price || 0) > Number(variant?.price || 0);

    if (priceSep) priceSep.hidden = false;
    if (priceEl) priceEl.textContent = formattedPrice;

    if (compareEl) {
      if (showCompare) {
        compareEl.textContent = formatMoney(variant.compare_at_price, getCurrencyCodeEnabled(form));
        compareEl.classList.remove('hide');
      } else {
        compareEl.classList.add('hide');
      }
    }

    addToCartBtn.setAttribute('aria-label', `${label} ${formattedPrice}`.trim());
  };

  const syncVariantPrice = (form, variant) => {
    if (!variant) return;

    const useCurrency = getCurrencyCodeEnabled(form);
    const formattedPrice = formatMoney(variant.price, useCurrency);

    const cfPrice = form.querySelector('[data-cf-variant-price]');
    if (cfPrice) cfPrice.textContent = formattedPrice;

    syncButtonLabel(form, variant, formattedPrice);
  };

  const initProgressBars = (root = document) => {
    root.querySelectorAll('[data-cf-progress]').forEach((el) => {
      if (el.dataset.cfInitialised === '1') return;
      const fill = el.querySelector('[data-cf-fill]');
      if (!fill) return;
      const targetWidth = fill.dataset.targetWidth || '0%';
      fill.style.width = '0%';
      requestAnimationFrame(() => {
        el.classList.add('is-ready');
        fill.style.setProperty('--cf-fill-width', targetWidth);
        fill.style.width = targetWidth;
      });
      el.dataset.cfInitialised = '1';
    });
  };

  const initCountdowns = (root = document) => {
    root.querySelectorAll('#cf-countdown').forEach((el) => {
      if (el.dataset.cfInitialised === '1') return;
      const target = new Date(el.dataset.target);
      if (!el.dataset.target || Number.isNaN(target.getTime())) {
        el.hidden = true;
        return;
      }

      const daysEl = el.querySelector('[data-cf-days]');
      const hoursEl = el.querySelector('[data-cf-hours]');
      const minsEl = el.querySelector('[data-cf-mins]');
      const secsEl = el.querySelector('[data-cf-secs]');

      const pad = (n) => String(n).padStart(2, '0');
      const render = () => {
        const diff = target.getTime() - Date.now();
        if (diff <= 0) {
          if (daysEl) daysEl.textContent = '00';
          if (hoursEl) hoursEl.textContent = '00';
          if (minsEl) minsEl.textContent = '00';
          if (secsEl) secsEl.textContent = '00';
          return false;
        }

        const totalSeconds = Math.floor(diff / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        if (daysEl) daysEl.textContent = pad(days);
        if (hoursEl) hoursEl.textContent = pad(hours);
        if (minsEl) minsEl.textContent = pad(mins);
        if (secsEl) secsEl.textContent = pad(secs);
        return true;
      };

      render();
      const interval = window.setInterval(() => {
        if (!render()) {
          window.clearInterval(interval);
          el.hidden = true;
        }
      }, 1000);
      el.dataset.cfInitialised = '1';
    });
  };

  const bindVariantSync = (form) => {
    const section = getSection(form);
    if (!section || section.dataset.cfVariantSyncBound === '1') return;

    const syncFromEvent = (event) => {
      const variant = event?.detail?.variant || getSelectedVariant(form);
      syncVariantPrice(form, variant);
    };

    section.addEventListener('variantChange', syncFromEvent);
    section.addEventListener('variantPriceChange', syncFromEvent);
    section.dataset.cfVariantSyncBound = '1';
  };

  const initSellingPlanSelection = (root = document) => {
    root.querySelectorAll(SELECTORS.form).forEach((form) => {
      if (!form.dataset.cfSellingPlanId && !form.querySelector('input[name="selling_plan"]')) return;
      if (form.dataset.cfSellingPlanInitialised === '1') return;

      const radios = [...form.querySelectorAll('input[name="selling_plan"]')];
      const defaultRadio = radios.find((input) => !input.value);
      const activePlanId = (form.dataset.cfSellingPlanId || '').trim();
      const selectedPlanRadio = activePlanId
        ? radios.find((input) => input.value === activePlanId) || radios.find((input) => input.value)
        : radios.find((input) => input.value);

      if (selectedPlanRadio) {
        selectedPlanRadio.checked = true;
        if (defaultRadio) defaultRadio.checked = false;
        selectedPlanRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      form.dataset.cfSellingPlanInitialised = '1';
    });
  };

  const initCrowdfundPdp = (root = document) => {
    initProgressBars(root);
    initCountdowns(root);
    initSellingPlanSelection(root);

    root.querySelectorAll(SELECTORS.form).forEach((form) => {
      if (!form.dataset.cfSellingPlanId && !form.querySelector('[data-cf-variant-price]')) return;
      bindVariantSync(form);
      syncVariantPrice(form, getSelectedVariant(form));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCrowdfundPdp());
  } else {
    initCrowdfundPdp();
  }

  document.addEventListener('shopify:section:load', (event) => initCrowdfundPdp(event.target));
  document.addEventListener('shopify:section:select', (event) => initCrowdfundPdp(event.target));
})();
